<template>
  <div class="grid grid-rows my-4 order-history overflow-x-auto mx-auto w-10/12">
    <!-- Message List View -->
    <div v-if="!selectedMessage">
      <div class="md:flex md:items-center md:justify-between">
        <div class="min-w-0 flex mb-2">
          <h2 class="text-2xl font-bold text-gray-900 sm:truncate sm:tracking-tight">Berichten</h2>
        </div>
        <!-- Tab Navigation -->
        <div class="flex items-center ml-auto">
          <div class="flex bg-white rounded-full border border-gray-200 p-1">
            <button
              @click="$emit('switch-view', 'dashboard')"
              class="flex items-center px-4 py-2 text-sm font-medium text-gray-600 rounded-full hover:text-gray-900 hover:bg-gray-50 transition-colors"
              :class="{ 'bg-gray-600 text-white': false }"
            >
              <ClipboardDocumentListIcon class="w-4 h-4 mr-2" />
              Dashboard
            </button>
            <button
              @click="$emit('switch-view', 'messages')"
              class="flex items-center px-4 py-2 text-sm font-medium text-gray-600 rounded-full hover:text-gray-900 hover:bg-gray-50 transition-colors"
              :class="{ 'bg-gray-600 text-white': true }"
            >
              <ChatBubbleLeftRightIcon class="w-4 h-4 mr-2" />
              Berichten
            </button>
          </div>
        </div>
      </div>

      <div v-if="messages.length === 0" class="grid grid-rows p-6 mb-6 callout">
        Er zijn geen berichten beschikbaar voor deze patiënt.
      </div>

      <tables v-else class="overflow-hidden rounded-lg pt-4">
        <template #tableHeaders>
          <thead>
            <tr>
              <th
                scope="col"
                class="py-3.5 pl-4 pr-3 text-left text-base font-bold text-pdsPrimaryColor"
                v-for="header in messageHeaders"
                :key="header"
              >
                {{ header }}
              </th>
              <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-base font-bold text-pdsPrimaryColor w-48"></th>
            </tr>
          </thead>
        </template>
        <template #tableBody>
          <tbody class="bg-white divide-y divide-sky-200">
            <tr
              v-for="(message, index) in messages"
              :key="`message-${index}`"
              class="hover:bg-gray-50"
              :class="{ 'font-bold': !message.read }"
            >
              <td
                class="py-3.5 pl-4 pr-3 text-base text-gray-900 cursor-pointer"
                @click="selectMessage(message)"
              >
                {{ message.subject || 'Geen onderwerp' }}
              </td>
              <td
                class="py-3.5 pl-4 pr-3 text-base text-gray-900 cursor-pointer"
                @click="selectMessage(message)"
              >
                {{ message.patient_name || '-' }}
              </td>
              <td
                class="py-3.5 pl-4 pr-3 text-base text-gray-900 cursor-pointer"
                @click="selectMessage(message)"
              >
                {{ $formatDate ? $formatDate(message.received_at) : formatDate(message.received_at) }}
              </td>
              <td class="py-3.5 pl-4 pr-3 text-base text-gray-900">
                <div class="flex items-center gap-2 justify-end">
                  <button
                    @click.stop="selectMessage(message)"
                    class="text-pdsPrimaryColor hover:text-pdsPrimaryColor-700 underline text-sm font-medium"
                  >
                    Bekijk bericht
                  </button>
                  <div class="relative inline-block text-left">
                    <button
                      @click.stop="toggleDropdown(index)"
                      type="button"
                      class="inline-flex items-center justify-center rounded-full p-1 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-pdsPrimaryColor focus:ring-offset-2"
                      :aria-expanded="openDropdownIndex === index"
                      aria-haspopup="true"
                    >
                      <span class="sr-only">Open options</span>
                      <EllipsisVerticalIcon class="h-5 w-5" aria-hidden="true" />
                    </button>
                    <div
                      v-if="openDropdownIndex === index"
                      v-clickout="() => closeDropdown()"
                      class="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                      role="menu"
                      aria-orientation="vertical"
                      aria-labelledby="menu-button"
                    >
                      <div class="py-1" role="none">
                        <a
                          v-if="message.read"
                          href="#"
                          @click.stop.prevent="markAsUnread(message, index)"
                          class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          role="menuitem"
                        >
                          Markeer als ongelezen
                        </a>
                        <a
                          v-else
                          href="#"
                          @click.stop.prevent="markAsRead(message, index)"
                          class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          role="menuitem"
                        >
                          Markeer als gelezen
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </template>
      </tables>
    </div>

    <!-- Message Detail View -->
    <div v-else class="bg-white rounded-lg shadow-md p-6">
      <div class="mb-6">
        <button
          @click="goBack"
          class="flex items-center text-pdsPrimaryColor hover:text-pdsPrimaryColor-700 font-medium mb-4"
        >
          <span class="text-lg mr-2">‹</span>
          Terug naar alle berichten
        </button>
      </div>

      <div class="space-y-4">
        <div class="text-xl font-bold text-gray-900">
          {{ selectedMessage.subject || 'Geen onderwerp' }}
        </div>

        <div class="flex items-center">
          <span class="font-medium text-gray-700">Datum:</span>
          <span class="text-gray-900 ml-2">
            {{ $formatDate ? $formatDate(selectedMessage.received_at) : formatDate(selectedMessage.received_at) }}
          </span>
        </div>

        <div v-if="selectedMessage.patient_name" class="flex items-center">
          <span class="font-medium text-gray-700">Patiënt:</span>
          <span class="text-gray-900 ml-2">
            {{ selectedMessage.patient_name }}
          </span>
        </div>

        <div class="pt-4 text-gray-900 leading-relaxed" v-html="selectedMessage.body || selectedMessage.content || 'Geen inhoud beschikbaar'">
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { ClipboardDocumentListIcon, ChatBubbleLeftRightIcon, EllipsisVerticalIcon } from '@heroicons/vue/20/solid';
import { currentUserIdentity$, activeDepartment$ } from '@/services';

export default {
  name: 'PatientMessages',
  components: {
    ClipboardDocumentListIcon,
    ChatBubbleLeftRightIcon,
    EllipsisVerticalIcon
  },
  props: {
    patientId: {
      type: Number
    }
  },
  emits: ['switch-view'],
  data() {
    return {
      messages: [],
      selectedMessage: null,
      messageHeaders: ['Omschrijving', 'Patiënt', 'Datum'],
      user: null,
      activeDepartment: null,
      userSubscription: null,
      departmentSubscription: null,
      openDropdownIndex: null
    };
  },
  computed: {
    baseUrl() {
      return window.location.origin;
    }
  },
  async created() {
    // Subscribe to current user identity
    this.userSubscription = currentUserIdentity$.subscribe((currentUser) => {
      this.user = currentUser;
      if (currentUser && currentUser.id && this.activeDepartment) {
        this.fetchMessages();
      }
    });

    // Subscribe to active department
    this.departmentSubscription = activeDepartment$.subscribe((department) => {
      this.activeDepartment = department;
      if (this.user && this.user.id && department) {
        this.fetchMessages();
      }
    });
  },
  beforeUnmount() {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    if (this.departmentSubscription) {
      this.departmentSubscription.unsubscribe();
    }
  },
  methods: {
    async fetchMessages() {
      if (!this.user || !this.user.id || !this.activeDepartment) {
        return;
      }

      try {
        let url = `${this.baseUrl}/api/sitecore/v2/Notification/GetInboxMessages?patientId=${this.patientId}`;

        // Add userId if available
        if (this.user && this.user.id) {
          url += `&userId=${this.user.id}`;
        }

        // Add departmentId if available
        if (this.activeDepartment && this.activeDepartment.ppwRecId) {
          url += `&departmentId=${this.activeDepartment.ppwRecId}`;
        }

        const { data } = await axios.get(url);
        this.messages = data || [];
      } catch (error) {
        console.error('Error fetching messages:', error);
        this.messages = [];
      }
    },
    async selectMessage(message) {
      this.selectedMessage = message;

      // Mark message as read if it's not already read
      if (!message.read) {
        // Update the message in the array immediately to unbold it (optimistic update)
        const messageIndex = this.messages.findIndex(m => m.id === message.id);
        if (messageIndex !== -1) {
          this.messages = this.messages.map((m, idx) =>
            idx === messageIndex ? { ...m, read: true } : m
          );
        }
        // Also update the selected message
        message.read = true;

        // Mark as read on the server in the background
        try {
          await axios.post(`${this.baseUrl}/api/sitecore/v2/Notification/MarkMessageAsRead`, {
            patientId: this.patientId,
            messageId: message.id,
            inboxId: message.inbox_id || message.inboxId
          });
        } catch (error) {
          console.error('Error marking message as read:', error);
          // Don't revert - keep the optimistic update
        }
      }
    },
    goBack() {
      this.selectedMessage = null;
    },
    formatDate(dateString) {
      if (!dateString) return 'Onbekend';
      try {
        return new Date(dateString).toLocaleDateString('nl-NL');
      } catch (error) {
        return 'Onbekend';
      }
    },
    toggleDropdown(index) {
      this.openDropdownIndex = this.openDropdownIndex === index ? null : index;
    },
    closeDropdown() {
      this.openDropdownIndex = null;
    },
    async markAsRead(message, index) {
      this.closeDropdown();

      if (message.read) {
        return; // Already read
      }

      // Update the message in the array immediately (optimistic update)
      const messageIndex = this.messages.findIndex(m => m.id === message.id);
      if (messageIndex !== -1) {
        this.messages = this.messages.map((m, idx) =>
          idx === messageIndex ? { ...m, read: true } : m
        );
      }
      // Also update the message object
      message.read = true;

      // Mark as read on the server
      try {
        await axios.post(`${this.baseUrl}/api/sitecore/v2/Notification/MarkMessageAsRead`, {
          patientId: this.patientId,
          messageId: message.id,
          inboxId: message.inbox_id || message.inboxId
        });
      } catch (error) {
        console.error('Error marking message as read:', error);
        // Revert optimistic update on error
        if (messageIndex !== -1) {
          this.messages = this.messages.map((m, idx) =>
            idx === messageIndex ? { ...m, read: false } : m
          );
        }
        message.read = false;
      }
    },
    async markAsUnread(message, index) {
      this.closeDropdown();

      if (!message.read) {
        return; // Already unread
      }

      // Update the message in the array immediately (optimistic update)
      const messageIndex = this.messages.findIndex(m => m.id === message.id);
      if (messageIndex !== -1) {
        this.messages = this.messages.map((m, idx) =>
          idx === messageIndex ? { ...m, read: false } : m
        );
      }
      // Also update the message object
      message.read = false;

      // Note: There's no MarkMessageAsUnread endpoint for patient messages yet
      // For now, we're just updating the local state
      // TODO: If there's an API endpoint to mark patient message as unread, call it here
    }
  }
};
</script>
